import React, { useEffect, useState } from 'react';
import { observer } from 'mobx-react';
import { Button, Input, Tree } from 'antd';
import { CardLayout, cardLayoutOptions } from '../../Layout';
import { LinkComponent } from '../../LinkComponent';
import cx from 'classnames';
import { getCompanyHierarchyV2 } from '../../../middleware';
import { Catcher } from '../../Catcher';
import MainStyles from '../../styles/m_itemCardStyles.less';
import Styles from './m_styles.less';

const { Search } = Input;

export const CompaniesHierarchyV2: React.FC = observer(() => {
    const [companiesHierarchy, setCompaniesHierarchy] = useState<any>(null);
    const [treeData, setTreeData] = useState<any>(null);
    const [searchTerm, setSearchTerm] = useState('');
    const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
    const [autoExpandParent, setAutoExpandParent] = useState(true);

    const onExpand = (newExpandedKeys: React.Key[]) => {
        setExpandedKeys(newExpandedKeys);
        setAutoExpandParent(false);
    };

    function transformData(data: any, parentKey = '') {
        return (
            data?.map((item: any, index: number) => {
                const key = parentKey ? `${parentKey}-${index}` : `${index}`;
                return {
                    title: <LinkComponent currentListComponent={'CompaniesList'} id={item.id} name={item.name} />,
                    key,
                    children: item.children && item.children.length > 0 ? transformData(item.children, key) : [],
                };
            }) || []
        );
    }

    const getAllKeys = (nodes: any[]): React.Key[] => {
        let keys: React.Key[] = [];

        nodes.forEach((node) => {
            keys.push(node.key);
            if (node.children && node.children.length > 0) {
                keys = [...keys, ...getAllKeys(node.children)];
            }
        });

        return keys;
    };

    const fetchCompaniesHierarchy = async () => {
        const response = await getCompanyHierarchyV2();
        const transformed = transformData(response);
        setCompaniesHierarchy(transformed);
        setTreeData(transformed);

        const allKeys = getAllKeys(transformed);
        setExpandedKeys(allKeys);
    };

    const filterNodes = (nodes: any[], search: string, parentKeys: any[] = []): any => {
        const keys: any = [];
        const parentKeysForMatchedItems: any[] = [];

        const filteredList = nodes
            .map((node) => {
                const text = node?.title?.props.name;
                const matchesSearch = text.toLowerCase().includes(search.toLowerCase());

                const filteredChildren = node.children
                    ? filterNodes(node.children, search, [...parentKeys, node.key])
                    : { filteredList: [], keys: [] };

                if (matchesSearch) {
                    keys.push(node.key);
                    parentKeysForMatchedItems.push(...parentKeys, node.key);

                    const highlightedTitle = React.cloneElement(node.title, {
                        ...node.title.props,
                        name: highlightText(text, search),
                    });

                    return { ...node, title: highlightedTitle, children: node.children || [] };
                }

                keys.push(...filteredChildren.keys);

                parentKeysForMatchedItems.push(...filteredChildren.keys);

                if (filteredChildren.filteredList.length > 0) {
                    return { ...node, children: filteredChildren.filteredList };
                }

                return null;
            })
            .filter(Boolean);

        return { filteredList, keys, parentKeys: parentKeysForMatchedItems };
    };

    const onClear = () => {
        setTreeData(companiesHierarchy);
        setSearchTerm('');

        const allKeys = getAllKeys(companiesHierarchy);
        setExpandedKeys(allKeys);
        setAutoExpandParent(true);
    };

    const onSearch = (e: any) => {
        const search = e.target.value;

        if (e.target.value === '') {
            setTreeData(companiesHierarchy);
            setSearchTerm(search);

            const allKeys = getAllKeys(companiesHierarchy);
            setExpandedKeys(allKeys);
            setAutoExpandParent(true);
            return;
        }

        const { filteredList } = filterNodes(companiesHierarchy, search);

        setAutoExpandParent(true);
        setSearchTerm(search);

        const allKeys = getAllKeys(companiesHierarchy);
        setExpandedKeys(allKeys);
        setTreeData(filteredList);
    };

    const highlightText = (text: string, search: string) => {
        if (!search.trim()) return text;

        const regex = new RegExp(`(${search.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')})`, 'gi');
        const parts = text.split(regex);

        return parts.map((part, i) =>
            regex.test(part) ? (
                <span key={i} style={{ backgroundColor: '#FFFF00' }}>
                    {part}
                </span>
            ) : (
                part
            ),
        );
    };

    useEffect(() => {
        fetchCompaniesHierarchy();
    }, []);

    return (
        <Catcher>
            <div className={MainStyles.main}>
                <CardLayout
                    title={'Companies Hierarchy v2'}
                    columnOption={cardLayoutOptions.cardSupportPage}
                    idSpinners={['GET_COMPANY_HIERARCHY_V2']}
                >
                    <div className={Styles.searchContainer}>
                        <Search
                            style={{ marginBottom: 8 }}
                            placeholder="Search"
                            className={cx(Styles.searchInput, 'input-search')}
                            onChange={onSearch}
                            value={searchTerm}
                        />
                        <Button className={Styles.clearButton} onClick={onClear}>
                            Clear
                        </Button>
                    </div>
                    <Tree
                        onExpand={onExpand}
                        treeData={treeData}
                        defaultExpandAll={true}
                        autoExpandParent={autoExpandParent}
                        expandedKeys={expandedKeys}
                        showLine={true}
                    />
                </CardLayout>
            </div>
        </Catcher>
    );
});
